import { useQueryClient } from '@tanstack/react-query';
import { useEntityForm } from '@/shared/lib/useEntityForm';
import { ErrorList } from '@/shared/ui/ErrorList';
import { FileUploadField } from '@/shared/ui/FileUploadField';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { FormField } from '@/shared/ui/FormField';
import { ModalDialog } from '@/shared/ui/ModalDialog';
import { TextField } from '@/shared/ui/TextField';
import {
  PRIZE_PERIOD_OPTIONS,
  deletePrizeImage,
  savePrize,
  uploadPrizeImage,
  type PrizeValues,
} from './api';

const EDIT_CONFIRM = 'Вы уверены, что хотите сохранить изменения?';
const PERIOD_SELECT_WIDTH = 238;

type Props = {
  /** Set when editing an existing prize. */
  id?: number;
  initial: PrizeValues;
  onClose: () => void;
};

export const PrizeModal = ({ id, initial, onClose }: Props) => {
  const qc = useQueryClient();
  const { values, errors, setField, save, isSaving } = useEntityForm(
    initial,
    (v: PrizeValues) => savePrize(id, v),
    () => {
      void qc.invalidateQueries({ queryKey: ['raffle-prizes'] });
      onClose();
    },
  );

  const onSave = () => {
    if (!id || window.confirm(EDIT_CONFIRM)) save();
  };

  return (
    <ModalDialog title={id ? 'Редактирование приза' : 'Добавление приза'} onClose={onClose} onSave={onSave} isSaving={isSaving}>
      <TextField
        label="Название *"
        multiline
        rows={3}
        value={values.name}
        errors={errors.name}
        onChange={(v) => setField('name', v)}
      />
      <TextField
        label="Сколько будет разыграно (в месяц)"
        value={values.monthLimit}
        errors={errors.month_limit}
        onChange={(v) => setField('monthLimit', v, 'month_limit')}
      />
      <FileUploadField
        label="Фото *"
        files={values.image}
        errors={errors.image_id}
        previewWidth={85}
        upload={uploadPrizeImage}
        remove={deletePrizeImage}
        onChange={(files) => setField('image', files, 'image_id')}
      />
      <h4>Условия для получения приза</h4>
      <p>1. Минимальное количество оплат клиента за период (в рублях)</p>
      <TextField
        label="Количество оплат"
        value={values.minPaymentsSum}
        errors={errors.min_payments_sum}
        onChange={(v) => setField('minPaymentsSum', v, 'min_payments_sum')}
      />
      <FormField label="Период" errors={errors.min_payments_time}>
        <FilterSelect
          options={PRIZE_PERIOD_OPTIONS}
          value={values.minPaymentsTime}
          isClearable={false}
          width={PERIOD_SELECT_WIDTH}
          onChange={(v) => setField('minPaymentsTime', v, 'min_payments_time')}
        />
      </FormField>
      <p>2. Минимальное количество заявок клиента со статусом «Закрыто»</p>
      <TextField
        label="Количество заявок"
        value={values.minCloseBidCount}
        errors={errors.min_close_bid_count}
        onChange={(v) => setField('minCloseBidCount', v, 'min_close_bid_count')}
      />
      <FormField label="Период" errors={errors.min_close_bid_time}>
        <FilterSelect
          options={PRIZE_PERIOD_OPTIONS}
          value={values.minCloseBidTime}
          isClearable={false}
          width={PERIOD_SELECT_WIDTH}
          onChange={(v) => setField('minCloseBidTime', v, 'min_close_bid_time')}
        />
      </FormField>
      <ErrorList errors={{ common: errors.common ?? [], server: errors.server ?? [] }} />
    </ModalDialog>
  );
};
